/***************************************************************************************
 * FileName      : PlaybackPanel.tsx
 * Description   : 플레이백 패널 컴포넌트 (PlaybackControls와 PlaybackTrack 통합)
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.11
 * Modified Date : 2026.02.12 헤더에 현재 단계 뱃지 표시, 트랙 접기/펼치기 토글 추가, 기본 숨김
 * Reference     : 
 ***************************************************************************************/

import React, { useEffect, useState, useRef, useMemo } from 'react';
// 2026.02.11 : 박은정 : 플레이백 서비스 및 엔진 임포트
import { ScenarioService } from '../services/ScenarioService';
import { PlaybackEngine } from '../services/PlaybackEngine';
// 2026.02.11 : 박은정 : 플레이백 타입 임포트
import { ScenarioFile, PlaybackStatus, PlaybackSpeed, ScenarioPhase } from '../types/scenario';
// 2026.02.11 : 박은정 : 플레이백 컴포넌트 임포트
import { PlaybackControls } from './PlaybackControls';
import { PlaybackTrack } from './PlaybackTrack';
// 2026.02.12 : 박은정 : Phase 색상 유틸리티 임포트
import { getPhaseColor } from './PlaybackTrack';
// 2026.02.12 : 박은정 : 모니터링 데이터 타입 임포트 (순환 의존성 해소를 위해 types로 이동)
import { MonitoringData } from '../types/MonitoringData';
// 2026.05.13 : 박은정 : 시뮬레이션 값 변화 누적 시계열 타입 (차트는 좌측 모니터링 패널에서 렌더)
import { ChartPoint } from './PlaybackChart';

interface Props {
  scenarioId: string | null;
  onStateUpdate: (data: MonitoringData | null) => void;
  // 2026.05.13 : 박은정 : 차트 시계열을 부모(Main)가 보유 → MonitoringPanel에 전달
  onChartHistoryUpdate?: (updater: (prev: ChartPoint[]) => ChartPoint[]) => void;
}

// 2026.02.12 : 박은정 : PlaybackEngine 상태 → MonitoringData 변환 헬퍼
const toMonitoringData = (state: { sensors: Record<string, number>; controls: Record<string, number | boolean> }): MonitoringData => ({
  sensorValues: Object.fromEntries(
    Object.entries(state.sensors).map(([key, value]) => [key, String(value)])
  ),
  controlValues: Object.fromEntries(
    Object.entries(state.controls).map(([key, value]) => [
      key,
      typeof value === 'boolean' ? (value ? 'ON' : 'OFF') : String(value),
    ])
  ),
});

// 2026.02.12 : 박은정 : 현재 시간에 해당하는 활성 Phase 찾기
const getActivePhase = (currentTime: number, phases: ScenarioPhase[]): ScenarioPhase | null => {
  const active = phases.find(p => currentTime >= p.startTime && currentTime < p.endTime);
  if (active) return active;
  // 마지막 phase의 endTime에 도달한 경우 마지막 phase 반환
  if (phases.length > 0 && currentTime >= phases[phases.length - 1].startTime) {
    return phases[phases.length - 1];
  }
  return phases[0] || null;
};

// 2026.02.11 : 박은정 : 플레이백 패널 컴포넌트
export const PlaybackPanel: React.FC<Props> = ({
  scenarioId,
  onStateUpdate,
  onChartHistoryUpdate,
}) => {
  // 2026.02.11 : 박은정 : 플레이백 엔진 인스턴스 (ref로 관리)
  const engineRef = useRef<PlaybackEngine | null>(null);
  
  // 2026.02.11 : 박은정 : 시나리오 상태
  const [scenario, setScenario] = useState<ScenarioFile | null>(null);
  
  // 2026.02.11 : 박은정 : 플레이백 상태
  const [status, setStatus] = useState<PlaybackStatus>('stopped');
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [speed, setSpeed] = useState<PlaybackSpeed>(1);

  // 2026.02.12 : 박은정 : 트랙 상세 영역 접기/펼치기 상태 (기본: 접힘)
  const [isTrackExpanded, setIsTrackExpanded] = useState<boolean>(false);

  // 2026.02.12 : 박은정 : 현재 활성 Phase 계산 (메모이제이션)
  const activePhase = useMemo(() => {
    if (!scenario) return null;
    return getActivePhase(currentTime, scenario.phases);
  }, [currentTime, scenario]);

  // 2026.02.11 : 박은정 : 시나리오 로드
  useEffect(() => {
    if (!scenarioId) {
      setScenario(null);
      setStatus('stopped');
      setCurrentTime(0);
      onStateUpdate(null);
      // 2026.05.13 : 박은정 : 시나리오 해제 시 그래프 누적 초기화 (부모 state)
      onChartHistoryUpdate?.(() => []);

      // 기존 엔진 정리
      if (engineRef.current) {
        engineRef.current.dispose();
        engineRef.current = null;
      }
      return undefined;
    }

    // 2026.05.13 : 박은정 : 시나리오 진입(전환 포함) 시 그래프 누적 초기화 — 새 시뮬레이션은 t=0부터.
    onChartHistoryUpdate?.(() => []);

    let isMounted = true;

    const loadScenario = async () => {
      try {
        const scenarioService = ScenarioService.getInstance();
        const loadedScenario = await scenarioService.loadScenario(scenarioId);
        
        if (!isMounted) return;
        
        setScenario(loadedScenario);

        // 플레이백 엔진 생성 및 초기화
        if (engineRef.current) {
          engineRef.current.dispose();
        }
        
        const engine = new PlaybackEngine();
        engine.load(loadedScenario);
        
        // 콜백 설정
        engine.onStateUpdate((state) => {
          if (!isMounted) return;

          // 2026.02.12 : 박은정 : 헬퍼 함수로 변환 로직 통합
          onStateUpdate(toMonitoringData(state));

          // 2026.05.13 : 박은정 : 재생 중인 동안만 그래프 시계열에 누적.
          // - 'stopped'/'paused' 콜백(load/stop/pause/seek-pause) 시 누적 안 함.
          // - 시간이 직전 포인트보다 뒤로 가면 무시(seek-backward 보호).
          if (engine.getStatus() === 'playing' && onChartHistoryUpdate) {
            const t = engine.getCurrentTime();
            const point: ChartPoint = { time: t };
            for (const [k, v] of Object.entries(state.sensors)) {
              point[k] = v;
            }
            for (const [k, v] of Object.entries(state.controls)) {
              point[k] = typeof v === 'boolean' ? (v ? 1 : 0) : v;
            }
            onChartHistoryUpdate(prev => {
              const lastTime = prev.length > 0 ? prev[prev.length - 1].time : -1;
              if (point.time <= lastTime) return prev;
              return [...prev, point];
            });
          }
        });
        
        engine.onTimeUpdate((time) => {
          if (!isMounted) return;
          setCurrentTime(time);
        });
        
        engine.onStatusUpdate((newStatus) => {
          if (!isMounted) return;
          setStatus(newStatus);
        });
        
        engineRef.current = engine;
        setCurrentTime(engine.getCurrentTime());
        setStatus(engine.getStatus());
        
        // 2026.02.12 : 박은정 : 초기 상태 전달 (헬퍼 함수 사용)
        onStateUpdate(toMonitoringData(engine.getCurrentState()));
      } catch (error) {
        console.error('시나리오 로드 실패:', error);
      }
    };

    loadScenario();

    // 2026.02.11 : 박은정 : 컴포넌트 언마운트 시 정리
    return () => {
      isMounted = false;
      if (engineRef.current) {
        engineRef.current.dispose();
        engineRef.current = null;
      }
      // 2026.05.13 : 박은정 : PlaybackPanel 언마운트(시나리오 아코디언 닫힘 포함) 시 차트 제거
      onChartHistoryUpdate?.(() => []);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId]);

  // 2026.02.11 : 박은정 : 재생 핸들러
  // 2026.05.14 : 박은정 : 끝까지 재생된 상태에서 다시 재생 시 차트 누적 초기화 (engine 도 처음부터 재시작)
  const handlePlay = () => {
    if (!engineRef.current || !scenario) return;
    if (engineRef.current.getCurrentTime() >= scenario.meta.totalDuration) {
      onChartHistoryUpdate?.(() => []);
    }
    engineRef.current.play();
  };

  // 2026.02.11 : 박은정 : 일시정지 핸들러
  const handlePause = () => {
    if (engineRef.current) {
      engineRef.current.pause();
    }
  };

  // 2026.02.11 : 박은정 : 정지 핸들러
  const handleStop = () => {
    if (engineRef.current) {
      engineRef.current.stop();
    }
    // 2026.05.13 : 박은정 : 정지 시 그래프 누적 초기화 (부모 state, 재시작 시 깨끗한 차트)
    onChartHistoryUpdate?.(() => []);
  };

  // 2026.02.11 : 박은정 : 시크 핸들러
  const handleSeek = (time: number) => {
    if (engineRef.current) {
      engineRef.current.seekTo(time);
    }
  };

  // 2026.02.11 : 박은정 : 재생 속도 변경 핸들러
  const handleSpeedChange = (newSpeed: PlaybackSpeed) => {
    setSpeed(newSpeed);
    if (engineRef.current) {
      engineRef.current.setSpeed(newSpeed);
    }
  };

  // 2026.02.11 : 박은정 : 시나리오가 없으면 표시하지 않음
  if (!scenario) {
    return null;
  }

  return (
    <div className="playback-panel">
      {/* 2026.02.12 : 박은정 : 헤더 (시나리오 이름 + 현재 단계 뱃지 + 자세히 보기 토글) */}
      <div className="playback-panel__header">
        <h3 className="playback-panel__title">{scenario.meta.name}</h3>
        {/* 2026.02.12 : 박은정 : 현재 활성 단계 뱃지 */}
        {activePhase && (
          <span
            className="playback-panel__phase-badge"
            style={{ backgroundColor: getPhaseColor(activePhase.phaseId) }}
          >
            {activePhase.name}
          </span>
        )}
        {/* 2026.02.12 : 박은정 : 트랙 상세 보기 토글 버튼 */}
        <button
          className="playback-panel__toggle-btn"
          onClick={() => setIsTrackExpanded(!isTrackExpanded)}
          title={isTrackExpanded ? '트랙 접기' : '트랙 자세히 보기'}
        >
          <span className="playback-panel__toggle-text">
            {isTrackExpanded ? '접기' : '자세히'}
          </span>
          <svg
            className={`playback-panel__toggle-icon ${isTrackExpanded ? 'playback-panel__toggle-icon--expanded' : ''}`}
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </div>
      
      <div className="playback-panel__content">
        {/* 2026.02.12 : 박은정 : 플레이백 컨트롤 (phases 전달 → 재생바 상단 단계 표시 및 색상 변경) */}
        <PlaybackControls
          status={status}
          currentTime={currentTime}
          totalDuration={scenario.meta.totalDuration}
          speed={speed}
          phases={scenario.phases}
          onPlay={handlePlay}
          onPause={handlePause}
          onStop={handleStop}
          onSeek={handleSeek}
          onSpeedChange={handleSpeedChange}
        />

        {/* 2026.02.12 : 박은정 : 플레이백 트랙 (기본 숨김, 토글로 펼침) */}
        {isTrackExpanded && (
          <PlaybackTrack
            totalDuration={scenario.meta.totalDuration}
            currentTime={currentTime}
            phases={scenario.phases}
            events={engineRef.current?.getTimelineEvents() || []}
            initialState={scenario.initialState}
            onSeek={handleSeek}
          />
        )}

        {/* 2026.05.13 : 박은정 : 값 변화 누적 그래프는 좌측 MonitoringPanel 최상단에 표시 */}
      </div>
    </div>
  );
};
